import {cn} from '@/lib/utils';
import {useState, useEffect} from 'react';
import DOMPurify from 'dompurify';
import {Button} from './button';
import {Input} from './input';
import {useToast} from '@/hooks/use-toast';
import {useEditor, EditorContent} from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import Strike from '@tiptap/extension-strike';
import Placeholder from '@tiptap/extension-placeholder';
import ImageExtension from '@tiptap/extension-image';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  List,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Link,
  Image,
  Quote,
  Code,
  Type,
  Trash2,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './dialog';

interface DescriptionEditorProps {
  value: string;
  title?: string;
  isEditable?: boolean;
  isTitleEditable?: boolean;
  onChange?: (value: string) => void;
  onTitleChange?: (title: string) => void;
  placeholder?: string;
  showDeleteButton?: boolean;
  onDelete?: () => void;
  error?: string;
  /** When true, show red asterisk for required title (e.g. section title) */
  titleRequired?: boolean;
  /** When true, show "Content" label with red asterisk for required content */
  contentRequired?: boolean;
}

/** The heading level the cursor sits in, as the block-type select's value. */
const activeHeadingValue = (
  editor: {isActive: (name: string, attrs?: object) => boolean} | null,
): string => {
  if (editor?.isActive('heading', {level: 1})) return '1';
  if (editor?.isActive('heading', {level: 2})) return '2';
  if (editor?.isActive('heading', {level: 3})) return '3';
  return 'normal';
};

/**
 * A formatting button in the editor toolbar, shaded while its mark is active.
 * One component so the active-state styling cannot drift between buttons.
 */
const ToolbarButton = ({
  active,
  onClick,
  title,
  children,
}: Readonly<{
  active?: boolean;
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}>) => (
  <Button
    variant="ghost"
    size="sm"
    className={cn('h-6 w-6 p-0', active ? 'bg-gray-200' : '')}
    onClick={onClick}
    title={title}
  >
    {children}
  </Button>
);

export default function DescriptionEditor({
  value,
  title = 'Description',
  isEditable = false,
  isTitleEditable = true,
  onChange,
  onTitleChange,
  placeholder = 'Enter description...',
  showDeleteButton = false,
  onDelete,
  error,
  titleRequired = false,
  contentRequired = false,
}: DescriptionEditorProps) {
  const {toast} = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [editorTitle, setEditorTitle] = useState(title);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [pendingLink, setPendingLink] = useState('');
  const [pendingImageUrl, setPendingImageUrl] = useState('');

  // Initialize TipTap editor
  const editor = useEditor({
    extensions: [
      StarterKit,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Underline,
      Strike,
      ImageExtension.configure({
        HTMLAttributes: {
          class: 'max-w-full h-auto rounded shadow-sm',
        },
      }),
      Placeholder.configure({
        placeholder: placeholder,
        emptyEditorClass: 'is-editor-empty',
      }),
    ],
    content: value,
    editable: isEditing,
    onUpdate: ({editor}) => {
      const html = editor.getHTML();
      onChange?.(html);
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[120px] p-3',
      },
    },
  });

  // Update editor content when value prop changes
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  // Update editor editable state when isEditing changes
  useEffect(() => {
    if (editor) {
      editor.setEditable(isEditing);
      if (isEditing) {
        // Focus the editor when it becomes editable
        setTimeout(() => {
          editor.commands.focus();
        }, 100);
      }
    }
  }, [isEditing, editor]);

  const handleSave = () => {
    if (editor) {
      const html = editor.getHTML();
      onChange?.(html);
    }
    onTitleChange?.(editorTitle);
    setIsEditing(false);
  };

  const handleCancel = () => {
    if (editor) {
      editor.commands.setContent(value);
    }
    setEditorTitle(title);
    setIsEditing(false);
  };

  const handleEdit = () => {
    setIsEditing(true);
    // Focus the editor after a short delay to ensure it's rendered
    setTimeout(() => {
      editor?.commands.focus();
    }, 100);
  };

  const handleTitleChange = (newTitle: string) => {
    setEditorTitle(newTitle);
    onTitleChange?.(newTitle);
  };

  // When main edit mode is active, automatically enter editing state
  useEffect(() => {
    if (isEditable && !isEditing) {
      setIsEditing(true);
    }
  }, [isEditable, isEditing]);

  if (!isEditable) {
    return (
      <div className="bg-white rounded-lg border p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-700">{title}{titleRequired && <span className="text-red-500">*</span>}</h3>
        </div>
        <div
          className="text-sm text-gray-600 leading-relaxed prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:ml-4 [&_ol]:list-decimal [&_ol]:ml-4 [&_li]:list-item [&_li]:mb-1 [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded [&_img]:shadow-sm"
          dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(value || placeholder)}}
        />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border">
      <div className="flex items-center justify-between gap-3 px-4 py-2 bg-gray-50 border-b rounded-t-lg">
        {isTitleEditable ? (
          <div className="flex items-center gap-1">
            <Input
              value={editorTitle}
              onChange={(e) => handleTitleChange(e.target.value)}
              className={`text-sm font-semibold h-6 px-2 py-1 border-0 bg-transparent focus:ring-0 focus:outline-none ${
                error ? 'text-red-600' : ''
              }`}
              placeholder="Enter title..."
            />
            {titleRequired && <span className="text-red-500">*</span>}
          </div>
        ) : (
          <h3 className={`text-sm font-semibold text-gray-700 ${
            error ? 'text-red-600' : ''
          }`}>
            {editorTitle}
            {titleRequired && <span className="text-red-500">*</span>}
          </h3>
        )}
        {showDeleteButton && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onDelete}
            className="h-6 w-6 p-0 text-red-600 hover:text-red-800 hover:bg-red-50"
            title="Delete section"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      {isEditing ? (
        <div className="p-4">
          {contentRequired && (
            <p className="text-xs font-medium text-muted-foreground mb-2">Content <span className="text-red-500">*</span></p>
          )}
          {/* Toolbar */}
          <div className="flex items-center gap-1 mb-3 p-2 bg-gray-50 rounded border">
            <select
              className="text-xs border-0 bg-transparent focus:outline-none"
              value={activeHeadingValue(editor)}
              onChange={(e) => {
                const level = e.target.value;
                if (level === 'normal') {
                  editor?.chain().focus().setParagraph().run();
                } else {
                  editor
                    ?.chain()
                    .focus()
                    .toggleHeading({level: parseInt(level) as 1 | 2 | 3})
                    .run();
                }
              }}
            >
              <option value="normal">Normal</option>
              <option value="1">Heading 1</option>
              <option value="2">Heading 2</option>
              <option value="3">Heading 3</option>
            </select>

            <div className="w-px h-4 bg-gray-300 mx-1" />

            <ToolbarButton
              active={editor?.isActive('bold')}
              onClick={() => editor?.chain().focus().toggleBold().run()}
              title="Bold"
            >
              <Bold className="h-3 w-3" />
            </ToolbarButton>
            <ToolbarButton
              active={editor?.isActive('italic')}
              onClick={() => editor?.chain().focus().toggleItalic().run()}
              title="Italic"
            >
              <Italic className="h-3 w-3" />
            </ToolbarButton>
            <ToolbarButton
              active={editor?.isActive('underline')}
              onClick={() => editor?.chain().focus().toggleUnderline().run()}
              title="Underline"
            >
              <UnderlineIcon className="h-3 w-3" />
            </ToolbarButton>
            <ToolbarButton
              active={editor?.isActive('strike')}
              onClick={() => editor?.chain().focus().toggleStrike().run()}
              title="Strikethrough"
            >
              <Strikethrough className="h-3 w-3" />
            </ToolbarButton>

            <div className="w-px h-4 bg-gray-300 mx-1" />

            <ToolbarButton
              active={editor?.isActive('bulletList')}
              onClick={() => editor?.chain().focus().toggleBulletList().run()}
              title="Bullet List"
            >
              <List className="h-3 w-3" />
            </ToolbarButton>
            <ToolbarButton
              active={editor?.isActive('orderedList')}
              onClick={() => editor?.chain().focus().toggleOrderedList().run()}
              title="Numbered List"
            >
              <ListOrdered className="h-3 w-3" />
            </ToolbarButton>

            <div className="w-px h-4 bg-gray-300 mx-1" />

            <ToolbarButton
              active={editor?.isActive({textAlign: 'left'})}
              onClick={() => editor?.chain().focus().setTextAlign('left').run()}
              title="Align Left"
            >
              <AlignLeft className="h-3 w-3" />
            </ToolbarButton>
            <ToolbarButton
              active={editor?.isActive({textAlign: 'center'})}
              onClick={() => editor?.chain().focus().setTextAlign('center').run()}
              title="Align Center"
            >
              <AlignCenter className="h-3 w-3" />
            </ToolbarButton>
            <ToolbarButton
              active={editor?.isActive({textAlign: 'right'})}
              onClick={() => editor?.chain().focus().setTextAlign('right').run()}
              title="Align Right"
            >
              <AlignRight className="h-3 w-3" />
            </ToolbarButton>

            <div className="w-px h-4 bg-gray-300 mx-1" />

            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={() => {
                setPendingLink('');
                setShowLinkDialog(true);
              }}
              title="Insert Link"
            >
              <Link className="h-3 w-3" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={() => {
                setPendingImageUrl('');
                setShowImageDialog(true);
              }}
              title="Insert Image"
            >
              <Image className="h-3 w-3" />
            </Button>
            <ToolbarButton
              active={editor?.isActive('blockquote')}
              onClick={() => editor?.chain().focus().toggleBlockquote().run()}
              title="Insert Quote"
            >
              <Quote className="h-3 w-3" />
            </ToolbarButton>
            <ToolbarButton
              active={editor?.isActive('codeBlock')}
              onClick={() => editor?.chain().focus().toggleCodeBlock().run()}
              title="Insert Code Block"
            >
              <Code className="h-3 w-3" />
            </ToolbarButton>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={() =>
                editor?.chain().focus().clearNodes().unsetAllMarks().run()
              }
              title="Clear Formatting"
            >
              <Type className="h-3 w-3" />
            </Button>
          </div>

          {/* Editor Content */}
          <div className="min-h-[120px] border border-gray-200 rounded focus-within:ring-2 focus-within:ring-border focus-within:ring-offset-2 focus-within:border-input">
            <EditorContent
              editor={editor}
              className="prose prose-sm max-w-none focus:outline-none min-h-[120px] p-3 [&_.is-editor-empty:first-child::before]:text-gray-400 [&_.is-editor-empty:first-child::before]:content-[attr(data-placeholder)] [&_.is-editor-empty:first-child::before]:float-left [&_.is-editor-empty:first-child::before]:pointer-events-none [&_.is-editor-empty:first-child::before]:h-0 [&_ul]:list-disc [&_ul]:ml-4 [&_ol]:list-decimal [&_ol]:ml-4 [&_li]:list-item [&_li]:mb-1 [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded [&_img]:shadow-sm"
            />
          </div>
        </div>
      ) : (
        <div className="p-4">
          <div
            className="text-sm text-gray-600 leading-relaxed prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:ml-4 [&_ol]:list-decimal [&_ol]:ml-4 [&_li]:list-item [&_li]:mb-1 [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded [&_img]:shadow-sm"
            dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(value || placeholder)}}
          />
        </div>
      )}
      
      {/* Error message */}
      {error && (
        <div className="px-4 pb-2">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Insert Link</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Input
              value={pendingLink}
              onChange={(e) => setPendingLink(e.target.value)}
              placeholder="Enter URL (https://...)"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLinkDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                const url = pendingLink.trim();
                if (!url) {
                  setShowLinkDialog(false);
                  return;
                }
                editor?.chain().focus().setLink({href: url}).run();
                setShowLinkDialog(false);
              }}
            >
              Insert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showImageDialog} onOpenChange={setShowImageDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Insert Image</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Input
              value={pendingImageUrl}
              onChange={(e) => setPendingImageUrl(e.target.value)}
              placeholder="Enter image URL (https://...)"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImageDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                const cleanSrc = pendingImageUrl.replace(/^@/, '').trim();
                if (!cleanSrc) {
                  setShowImageDialog(false);
                  return;
                }
                const urlPattern = /^https?:\/\/.+\..+/;
                if (!urlPattern.test(cleanSrc)) {
                  toast({
                    title: 'Invalid URL',
                    description:
                      'Please enter a valid URL starting with http:// or https://',
                    variant: 'destructive',
                  });
                  return;
                }
                editor
                  ?.chain()
                  .focus()
                  .setImage({
                    src: cleanSrc,
                    alt: 'Image',
                    title: 'Image',
                  })
                  .run();
                setShowImageDialog(false);
              }}
            >
              Insert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
